import React from "react";

const COMPANY = "Santa Rosa Commercial Investment, Inc. (SRCI Pharm-Route)";
const SUPPORT_EMAIL = "santarosacommercial@gmail.com";

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-lg font-semibold text-slate-900 mb-2">{title}</h2>
      <div className="text-sm text-slate-700 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function TermsConditions() {
  return (
    <div data-testid="page-terms-conditions" className="min-h-screen bg-slate-50 px-6 py-12">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-12">
        <a href="/" className="text-xs text-primary font-medium mb-6 inline-block">&larr; Back to sign-in</a>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Terms &amp; Conditions</h1>
        <p className="text-xs text-slate-500 mb-8">Last updated: June 2026</p>

        <Section title="1. Acceptance of terms">
          <p>
            By accessing or using the {COMPANY} platform (the "Service"), you agree to be bound by
            these Terms &amp; Conditions ("Terms"). If you do not agree, do not use the Service.
          </p>
        </Section>

        <Section title="2. Service description">
          <p>
            The Service is a software platform for pharmacies to manage prescription deliveries —
            including order intake, route optimization, driver dispatch, proof-of-delivery capture,
            and billing. The Service is provided to participating pharmacies on a per-delivery
            fee basis.
          </p>
        </Section>

        <Section title="3. Eligible users and roles">
          <p>The Service is intended for authorized employees and contractors of participating pharmacies. User roles include:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li><strong>Super Admin</strong> — operator of the Service (SRCI staff)</li>
            <li><strong>Dispatcher</strong> — coordinates orders and routes</li>
            <li><strong>Pharmacy Staff</strong> — creates and tracks orders for their pharmacy</li>
            <li><strong>Driver</strong> — fulfills delivery routes</li>
          </ul>
        </Section>

        <Section title="4. User obligations">
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Keep account credentials confidential and report any suspected compromise immediately.</li>
            <li>Use the Service only for lawful purposes connected to your pharmacy delivery work.</li>
            <li>Provide accurate patient, pharmacy, and delivery information.</li>
            <li>Comply with all applicable laws including HIPAA, the Controlled Substances Act, and state pharmacy regulations.</li>
          </ul>
        </Section>

        <Section title="5. Fees and payment">
          <p>
            Pharmacy fees are billed per completed delivery according to the price band table
            configured for each pharmacy. Invoices are issued periodically (typically weekly or
            monthly) and may be paid via Zelle or credit card through Square (subject to a 3%
            processing surcharge for card payments). Past-due invoices may be subject to a late
            fee and service suspension.
          </p>
        </Section>

        <Section title="6. Acceptable use">
          <p>You agree not to:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Reverse-engineer, copy, or resell the Service.</li>
            <li>Use the Service to deliver illegal or restricted substances outside your DEA license scope.</li>
            <li>Upload viruses, malware, or attempt to compromise the Service or its data.</li>
            <li>Share another user's credentials or impersonate someone else.</li>
          </ul>
        </Section>

        <Section title="7. Intellectual property">
          <p>
            All software, designs, and trademarks comprising the Service are owned by {COMPANY}.
            You receive a limited, non-exclusive, non-transferable license to use the Service for
            your delivery operations during the term of your agreement with us.
          </p>
        </Section>

        <Section title="8. Disclaimer of warranties">
          <p>
            The Service is provided "as is" without warranties of any kind. We do not guarantee
            uninterrupted operation, error-free routing, or successful delivery of every package.
            The Service relies on third-party providers (Google Maps, OSRM, Twilio, Square, etc.)
            whose downtime is outside our control.
          </p>
        </Section>

        <Section title="9. Limitation of liability">
          <p>
            To the maximum extent permitted by law, {COMPANY}'s total liability arising out of
            the Service shall not exceed the fees you paid us in the 12 months preceding the
            claim. We are not liable for indirect, incidental, consequential, or punitive damages.
          </p>
        </Section>

        <Section title="10. Termination">
          <p>
            We may suspend or terminate your access for breach of these Terms or for non-payment.
            Upon termination, your right to access the Service ceases. PHI is retained or returned
            according to the Business Associate Agreement.
          </p>
        </Section>

        <Section title="11. Governing law">
          <p>
            These Terms are governed by the laws of the State of Florida, without regard to
            conflict-of-law principles. Any dispute shall be brought in the state or federal courts
            of Broward or Miami-Dade County, Florida.
          </p>
        </Section>

        <Section title="12. Changes">
          <p>
            We may revise these Terms from time to time. Material changes will be posted on this
            page and communicated to participating pharmacies. Continued use of the Service after
            the effective date constitutes acceptance.
          </p>
        </Section>

        <Section title="13. Contact">
          <p>
            Questions? Email{" "}
            <a className="text-primary underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
        </Section>

        <p className="text-xs text-slate-400 mt-8 italic">
          This is a starter template. Have your legal counsel review and customize before going live.
        </p>
      </div>
    </div>
  );
}
